import config from 'config/api.json';

/**
 * Gets an URL parameter
 * @param location the location
 * @param paramCode the parameter code to retrieve
 * @returns {null|*} the URL parameter value
 */
export function getUrlParameter(location, paramCode) {
  if (!location?.search) {
    return null;
  }
  return new URLSearchParams(location.search).get(paramCode);
}

/**
 * Removes specified query parameters from a given URL.
 *
 * This function takes a location object and an array of parameter names to remove from the URL's query string.
 * It manipulates the URL without reloading the page and returns the new URL as a string.
 *
 * @param {Location} location - The location object, typically from window.location.
 * @param {string[]} paramsToRemove - An array of strings representing the names of the query parameters to remove.
 * @param {string[]} uppercaseParams - An array of strings representing the names of the query parameters to uppercase.
 * @returns {string} The URL as a string with the specified query parameters removed.
 */
export function cleanUrlParameters(location, paramsToRemove, uppercaseParams = []) {
  const urlParams = new URLSearchParams(location.search);

  paramsToRemove.forEach((param) => {
    urlParams.delete(param);
  });

  uppercaseParams.forEach((param) => {
    if (urlParams.has(param)) {
      const value = urlParams.get(param).toUpperCase();
      urlParams.set(param, value);
    }
  });

  return location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '');
}

/**
 * Appends a request parameter to a URL
 * @param args
 * @param key
 * @param value
 * @returns {string|*|{url: (string|*)}}
 */
export function appendQueryStringParam(args, key, value) {
  let urlEnd = typeof args === 'string' ? args : args.url;

  if (urlEnd.indexOf('?') < 0) urlEnd += '?';
  else urlEnd += '&';

  urlEnd += `${key}=${value}`;

  return typeof args === 'string' ? urlEnd : { ...args, url: urlEnd };
}

/**
 * Gets the server base URL.
 */
export function getServerBaseUrl() {
  const contextPath = window.REACT_APP_CONTEXT_PATH || '';

  if (!DEV) {
    if (window.serverBaseUrl) {
      return cleanUrl(window.serverBaseUrl + contextPath);
    }
    return cleanUrl(window.location.origin + contextPath);
  }
  return cleanUrl(config.devServerBaseUrl);
}

/**
 * Gets the image URL.
 * @param imageCode the image code
 * @param appCode the app code
 * @returns {string}
 */
export function getImageURL(imageCode, appCode) {
  const appCodeParam = appCode ? `&app=${appCode}` : '';
  return cleanUrl(`${getServerBaseUrl()}/image?imageCode=${imageCode}${appCodeParam}`);
}

/**
 * Gets the background image URL.
 * @param number the number of the background image
 * @returns {string}
 */
export function getBgSignIn(number) {
  return cleanUrl(`${getServerBaseUrl()}/public/images/bg_${number}.webp`);
}

export function cleanUrl(url) {
  return url.replace(/([^:]\/)\/+/g, '$1');
}
